/**
 * Usage tracking type definitions.
 */

/**
 * Usage type enumeration.
 */
export const UsageType = {
  LLMTokens: 0,
  Storage: 1,
  ApiCalls: 2,
  ToolExecutions: 3,
  ChatCompaction: 4,
  ImageCompaction: 5,
  ImageDescription: 6,
} as const;

export type UsageType = typeof UsageType[keyof typeof UsageType];

/**
 * Aggregation period enumeration.
 */
export const AggregationPeriod = {
  Daily: 0,
  Weekly: 1,
  Monthly: 2,
} as const;

export type AggregationPeriod = typeof AggregationPeriod[keyof typeof AggregationPeriod];

/**
 * Usage record data transfer object.
 */
export interface UsageRecordDto {
  id: string;
  tenantId: string;
  projectId?: string;
  usageType: UsageType;
  recordDate: string;
  agentDefinitionId?: string;
  agentDefinitionName?: string;
  modelId?: string;
  modelName?: string;
  sessionId?: string;
  quantity: number;
  cost: number;
  metadataJson?: string;
  createdAt: string;
}

/**
 * Usage aggregation data transfer object.
 */
export interface UsageAggregationDto {
  id: string;
  tenantId: string;
  projectId?: string;
  usageType: UsageType;
  period: AggregationPeriod;
  periodStart: string;
  periodEnd: string;
  agentDefinitionId?: string;
  agentDefinitionName?: string;
  modelId?: string;
  modelName?: string;
  totalQuantity: number;
  totalCost: number;
  recordCount: number;
}

/**
 * Usage breakdown by agent.
 */
export interface UsageByAgentDto {
  agentDefinitionId: string;
  agentDefinitionName: string;
  totalQuantity: number;
  totalCost: number;
  recordCount: number;
}

/**
 * Usage breakdown by model.
 */
export interface UsageByModelDto {
  modelId: string;
  modelName: string;
  totalQuantity: number;
  totalCost: number;
  recordCount: number;
}

/**
 * Usage summary data transfer object.
 */
export interface UsageSummaryDto {
  tenantId: string;
  projectId?: string;
  usageType: UsageType;
  startDate: string;
  endDate: string;
  totalQuantity: number;
  totalCost: number;
  recordCount: number;
  byAgent: UsageByAgentDto[];
  byModel: UsageByModelDto[];
}

/**
 * Paginated result for usage records.
 */
export interface PagedUsageRecords {
  items: UsageRecordDto[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
}

/**
 * Query parameters for getting usage records.
 */
export interface GetUsageRecordsParams {
  projectId?: string;
  usageType?: UsageType;
  startDate?: string;
  endDate?: string;
  agentDefinitionId?: string;
  modelId?: string;
  pageNumber?: number;
  pageSize?: number;
}

/**
 * Query parameters for getting usage aggregations.
 */
export interface GetUsageAggregationsParams {
  projectId?: string;
  usageType?: UsageType;
  period?: AggregationPeriod;
  startDate?: string;
  endDate?: string;
  agentDefinitionId?: string;
  modelId?: string;
}

/**
 * Query parameters for getting usage summary.
 */
export interface GetUsageSummaryParams {
  projectId?: string;
  usageType?: UsageType;
  startDate: string;
  endDate: string;
}
